import { BaseApi } from './BaseApi';
import { ApiBaseResponse } from 'src/interfaces';

export interface IOrderStatusAnalytics {
    status_id: number;
    status_name: string;
    count: number;
    total_amount: number;
}

export interface IMonthlyAnalytics {
    month: string;
    month_number: number;
    count: number;
    total_amount: number;
    avg_amount: number;
}

export interface IManagerAnalytics {
    manager_id: number;
    manager_name: string;
    count: number;
    total_amount: number;
    avg_amount: number;
}

export interface IOrderTypeAnalytics {
    type: string;
    count: number;
    total_amount: number;
}

export interface IGeneralStats {
    total_orders: number;
    total_amount: number;
    avg_order_amount: number;
    completed_orders: number;
    canceled_orders: number;
    completion_rate: string;
    cancellation_rate: string;
    partner_testing_total?: number;
    orders_in_progress?: number;
}

export interface IProductAnalytics {
    product_id: number;
    product_name: string;
    product_model: string;
    count: number;
    total_amount: number;
    avg_price: number;
    total_revenue: number;
}

export interface ICategoryAnalytics {
    category_id: number;
    category_name: string;
    count: number;
    total_revenue: number;
}

export interface IManufacturerAnalytics {
    manufacturer_id: number;
    manufacturer_name: string;
    count: number;
    total_revenue: number;
}

export interface IProductMonthlyAnalytics {
    month: string;
    month_number: number;
    count: number;
    total_revenue: number;
    avg_price: number;
}

class AnalyticsApi extends BaseApi {
    constructor() {
        super('/analytics');
    }

    _getFilters(params?: Record<string, string>): string {
        return Object.entries(params ?? {}).reduce((acc, [key, value]) => {
            acc += value ? `${acc === '' ? '?' : '&'}${key}=${value}` : '';
            return acc;
        }, '');
    }

    async getOrderStatusAnalytics(params?: { dateFrom?: string; dateTo?: string }): Promise<ApiBaseResponse<IOrderStatusAnalytics[]> | undefined> {
        return this.errorHandler((await this.GET<ApiBaseResponse<IOrderStatusAnalytics[]>>(`/orders/status${this._getFilters(params)}`))?.data);
    }

    async getMonthlyAnalytics(params?: { year?: string }): Promise<ApiBaseResponse<IMonthlyAnalytics[]> | undefined> {
        return this.errorHandler((await this.GET<ApiBaseResponse<IMonthlyAnalytics[]>>(`/orders/monthly${this._getFilters(params)}`))?.data);
    }

    async getManagerAnalytics(params?: { dateFrom?: string; dateTo?: string }): Promise<ApiBaseResponse<IManagerAnalytics[]> | undefined> {
        return this.errorHandler((await this.GET<ApiBaseResponse<IManagerAnalytics[]>>(`/orders/managers${this._getFilters(params)}`))?.data);
    }

    async getOrderTypeAnalytics(params?: { dateFrom?: string; dateTo?: string }): Promise<ApiBaseResponse<IOrderTypeAnalytics[]> | undefined> {
        return this.errorHandler((await this.GET<ApiBaseResponse<IOrderTypeAnalytics[]>>(`/orders/types${this._getFilters(params)}`))?.data);
    }

    async getGeneralStats(params?: { dateFrom?: string; dateTo?: string }): Promise<ApiBaseResponse<IGeneralStats> | undefined> {
        return this.errorHandler((await this.GET<ApiBaseResponse<IGeneralStats>>(`/orders/general${this._getFilters(params)}`))?.data);
    }

    async getProductAnalytics(params?: { dateFrom?: string; dateTo?: string }): Promise<ApiBaseResponse<IProductAnalytics[]> | undefined> {
        return this.errorHandler((await this.GET<ApiBaseResponse<IProductAnalytics[]>>(`/products/analytics${this._getFilters(params)}`))?.data);
    }

    async getCategoryAnalytics(params?: { dateFrom?: string; dateTo?: string }): Promise<ApiBaseResponse<ICategoryAnalytics[]> | undefined> {
        return this.errorHandler((await this.GET<ApiBaseResponse<ICategoryAnalytics[]>>(`/products/categories${this._getFilters(params)}`))?.data);
    }

    async getManufacturerAnalytics(params?: { dateFrom?: string; dateTo?: string }): Promise<ApiBaseResponse<IManufacturerAnalytics[]> | undefined> {
        return this.errorHandler((await this.GET<ApiBaseResponse<IManufacturerAnalytics[]>>(`/products/manufacturers${this._getFilters(params)}`))?.data);
    }

    async getProductMonthlyAnalytics(params?: { year?: string }): Promise<ApiBaseResponse<IProductMonthlyAnalytics[]> | undefined> {
        return this.errorHandler((await this.GET<ApiBaseResponse<IProductMonthlyAnalytics[]>>(`/products/monthly${this._getFilters(params)}`))?.data);
    }
}

export const analyticsApi = new AnalyticsApi();
